import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { TablePagination } from "@mui/material";
import { getDefaultPlans, updateDefaultPlan } from "./Helper";

const DefaultPlans = () => {
    const context = useOutletContext();
    const [defaultPlans, setDefaultPlans] = useState([]);
    const [paymentProvider, setPaymentProvider] = useState('RAZORPAY');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);

    const [editPlan, setEditPlan] = useState(null);
    const [editForm, setEditForm] = useState({ amount: '', display_amount: '', gstAmount: '' });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const currencySymbol = (provider) =>
        provider?.toLowerCase() === 'stripe' ? '$' : '₹';

    const formatDate = (dateStr) =>
        dateStr
            ? new Date(dateStr)
                  .toLocaleString("en-GB", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                  })
                  .replace(",", "")
                  .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1")
                  .replace(" ", ", ")
            : "";

    const fetchData = (provider, currentPage, currentLimit) => {
        setLoading(true);
        getDefaultPlans(provider, currentPage, currentLimit)
            .then((response) => {
                setDefaultPlans(response?.data?.result || []);
                setTotal(response?.data?.meta?.total * response?.data?.meta?.pages || 0);
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchData(paymentProvider, newPage, limit);
    };

    const handleChangeRowsPerPage = (_event) => {
        const newLimit = parseInt(_event.target.value, 10);
        setLimit(newLimit);
        setPage(0);
        fetchData(paymentProvider, 0, newLimit);
    };

    const openEdit = (plan) => {
        setEditPlan(plan);
        setEditForm({
            amount: plan.amount,
            display_amount: plan.display_amount,
            gstAmount: plan.gstAmount ?? '',
        });
        setSaveError('');
    };

    const closeEdit = () => {
        setEditPlan(null);
        setSaveError('');
    };

    const handleSave = () => {
        setSaving(true);
        setSaveError('');
        const payload = {
            amount: parseFloat(editForm.amount),
            display_amount: parseFloat(editForm.display_amount),
            gstAmount: parseFloat(editForm.gstAmount),
        };
        updateDefaultPlan(editPlan.id, payload)
            .then((response) => {
                if (response?.data?.success) {
                    closeEdit();
                    fetchData(paymentProvider, page, limit);
                } else {
                    setSaveError('Update failed. Please try again.');
                }
            })
            .catch(() => setSaveError('Update failed. Please try again.'))
            .finally(() => setSaving(false));
    };

    useEffect(() => {
        context(["User Manager", "Default Plans"]);
        setPage(0);
        fetchData(paymentProvider, 0, limit);
    }, [paymentProvider]);

    return (
        <div className="p-5 shadow-md bg-white">
            <div className="flex flex-row justify-between gap-2 mb-4">
                <h3
                    className="col-span-3 w-full text-lg uppercase tracking-wider font-bold"
                    style={{ color: "#2EBAA3" }}
                >
                    Default Plans
                </h3>
                <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button
                        type="button"
                        onClick={() => setPaymentProvider('RAZORPAY')}
                        className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                            paymentProvider === 'RAZORPAY'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                        }`}
                    >
                        Razorpay
                    </button>
                    <button
                        type="button"
                        onClick={() => setPaymentProvider('STRIPE')}
                        className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                            paymentProvider === 'STRIPE'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                        }`}
                    >
                        Stripe
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-10 text-gray-500">Loading...</div>
            ) : (
                <div className="overflow-x-auto relative w-full">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th scope="col" className="py-3 px-6 uppercase">Sr.</th>
                                <th scope="col" className="py-3 px-6 uppercase whitespace-nowrap min-w-[160px]">Name</th>
                                <th scope="col" className="py-3 px-6 uppercase">Plan Id</th>
                                <th scope="col" className="py-3 px-6 uppercase">Type</th>
                                <th scope="col" className="py-3 px-6 uppercase">Display Amount</th>
                                <th scope="col" className="py-3 px-6 uppercase">Final Amount</th>
                                <th scope="col" className="py-3 px-6 uppercase">GST Amount</th>
                                <th scope="col" className="py-3 px-6 uppercase">Interval</th>
                                <th scope="col" className="py-3 px-6 uppercase">Payment Provider</th>
                                <th scope="col" className="py-3 px-6 uppercase">Status</th>
                                <th scope="col" className="py-3 px-6 uppercase whitespace-nowrap min-w-[160px]">Created At</th>
                                <th scope="col" className="py-3 px-6 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {defaultPlans.length === 0 ? (
                                <tr>
                                    <td colSpan={12} className="py-6 text-center text-gray-400">
                                        No default plans found.
                                    </td>
                                </tr>
                            ) : (
                                defaultPlans.map((plan, index) => (
                                    <tr className="bg-white border-b" key={plan.id}>
                                        <th scope="row" className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap">
                                            {index + 1}
                                        </th>
                                        <td className="py-4 px-6">{plan.name}</td>
                                        <td className="py-4 px-6 break-words" title={plan.PlanId}>{plan.PlanId}</td>
                                        <td className="py-4 px-6">{plan.type}</td>
                                        <td className="py-4 px-6">
                                            {currencySymbol(plan.payment_provider)}{plan.display_amount}
                                        </td>
                                        <td className="py-4 px-6">
                                            {currencySymbol(plan.payment_provider)}{plan.amount}
                                        </td>
                                        <td className="py-4 px-6">
                                            {plan.gstAmount
                                                ? `${currencySymbol(plan.payment_provider)}${plan.gstAmount}`
                                                : '-'}
                                        </td>
                                        <td className="py-4 px-6">{plan.interval?.toUpperCase()}</td>
                                        <td className="py-4 px-6">{plan.payment_provider}</td>
                                        <td className="py-4 px-6">
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                    plan.isActive
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-600'
                                                }`}
                                            >
                                                {plan.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6 whitespace-nowrap">{formatDate(plan.created_at)}</td>
                                        <td className="py-4 px-6">
                                            <button
                                                onClick={() => openEdit(plan)}
                                                className="bg-teal-500 hover:bg-teal-600 text-white text-xs font-medium px-3 py-1.5 rounded"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={limit}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />

            {/* Edit Modal */}
            {editPlan && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg font-bold mb-1 text-gray-800">Edit Plan</h2>
                        <p className="text-sm text-gray-500 mb-4">{editPlan.name}</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                                    Display Amount ({currencySymbol(editPlan.payment_provider)})
                                </label>
                                <input
                                    type="number"
                                    value={editForm.display_amount}
                                    onChange={(e) => setEditForm({ ...editForm, display_amount: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                                    Final Amount (incl. GST) ({currencySymbol(editPlan.payment_provider)})
                                </label>
                                <input
                                    type="number"
                                    value={editForm.amount}
                                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                                    GST Amount ({currencySymbol(editPlan.payment_provider)})
                                </label>
                                <input
                                    type="number"
                                    value={editForm.gstAmount}
                                    onChange={(e) => setEditForm({ ...editForm, gstAmount: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                                />
                            </div>
                        </div>

                        {saveError && (
                            <p className="text-red-500 text-sm mt-3">{saveError}</p>
                        )}

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={closeEdit}
                                disabled={saving}
                                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded hover:bg-teal-600 disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DefaultPlans;
